import React, {useCallback, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js'
import {Bar} from 'react-chartjs-2'
import {PageTitle} from '../../../_metronic/layout/core'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const API_URL = process.env.REACT_APP_API_URL
const DEFAULT_TARGET_PERCENT = 65
const WATCH_LOWER_PERCENT = 60

type OrganizationOption = {
  id: number
  name?: string | null
  years: number[]
}

type ProgramOutcome = {
  id: number
  order: number
  name?: string | null
  targetPercent?: number | null
  midtermPercent?: number | null
  finalPercent?: number | null
  achievementPercent?: number | null
  questionCount: number
  questionBankCount: number
  studentCount: number
  examCount: number
  hasMidterm: boolean
  hasFinal: boolean
  status: string
  statusLabel: string
  cycleCount: number
  openCycleCount: number
  latestCycleId?: number | null
}

type StudentOutcome = {
  organizationOutcomeId: number
  order: number
  name?: string | null
  midtermPercent?: number | null
  finalPercent?: number | null
  achievementPercent?: number | null
  questionCount: number
  examCount: number
  hasMidterm: boolean
  hasFinal: boolean
}

type StudentReport = {
  studentId: number
  studentNumber?: string | null
  studentName?: string | null
  averageAchievementPercent?: number | null
  outcomes: StudentOutcome[]
}

type ExamSummary = {
  examId: number
  examName?: string | null
  examTypeName?: string | null
  activityType: string
  activityTypeLabel: string
  weight: number
  semesterCourseId: number
  courseCode?: string | null
  courseName?: string | null
  studentCount: number
  questionCount: number
}

type Cycle = {
  id: number
  organizationId: number
  organizationOutcomeId: number
  year: number
  title?: string | null
  targetPercent: number
  currentPercent?: number | null
  status?: string | null
  plan?: string | null
  do?: string | null
  check?: string | null
  act?: string | null
  startedAt: string
  startedBy?: string | null
}

type SummaryInsight = {
  title: string
  value: string
  detail: string
  severity: string
}

type LowCourseFactor = {
  organizationOutcomeId: number
  outcomeOrder: number
  courseCode?: string | null
  courseName?: string | null
  activityType: string
  activityTypeLabel: string
  resultRowCount: number
  studentCount: number
  examCount: number
  questionCount: number
  rawPercent?: number | null
}

type ReportSummary = {
  targetPercent: number
  watchLowerBoundPercent: number
  outcomeCount: number
  dataOutcomeCount: number
  metOutcomeCount: number
  watchOutcomeCount: number
  lowOutcomeCount: number
  noDataOutcomeCount: number
  studentCount: number
  studentAveragePercent?: number | null
  studentMedianPercent?: number | null
  studentsBelowWatchCount: number
  studentsBelowTargetCount: number
  resultRowCount: number
  scoreFilledCount: number
  legacyOnlyCount: number
  scoreAbovePossibleCount: number
  negativeScoreCount: number
  insights: SummaryInsight[]
  lowCourseFactors: LowCourseFactor[]
}

type Report = {
  organizationId?: number | null
  organizationName?: string | null
  year?: number | null
  organizations: OrganizationOption[]
  years: number[]
  outcomes: ProgramOutcome[]
  students: StudentReport[]
  exams: ExamSummary[]
  cycles: Cycle[]
  summary?: ReportSummary | null
}

type CycleDraft = {
  id?: number
  organizationOutcomeId: number
  title: string
  targetPercent: number
  currentPercent?: number | null
  status: string
  plan: string
  do: string
  check: string
  act: string
}

const emptyReport: Report = {
  organizations: [],
  years: [],
  outcomes: [],
  students: [],
  exams: [],
  cycles: [],
}

const formatPercent = (value?: number | null, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-'
  }

  return `${value.toFixed(digits)}%`
}

const statusBadgeClass = (status?: string | null) => {
  if (status === 'met') {
    return 'badge-light-success'
  }

  if (status === 'watch') {
    return 'badge-light-warning'
  }

  if (status === 'low') {
    return 'badge-light-danger'
  }

  return 'badge-light-secondary'
}

const percentTextClass = (
  value?: number | null,
  target = DEFAULT_TARGET_PERCENT,
  watchLowerBound = WATCH_LOWER_PERCENT
) => {
  if (value === null || value === undefined) {
    return 'text-muted'
  }

  if (value < watchLowerBound) {
    return 'text-danger'
  }

  if (value < target) {
    return 'text-warning'
  }

  return 'text-success'
}

const buildCycleTitle = (outcome: ProgramOutcome) => `PÇ-${outcome.order} PUKÖ Döngüsü`

const insightBadgeClass = (severity?: string | null) => {
  if (severity === 'success') {
    return 'badge-light-success'
  }

  if (severity === 'warning') {
    return 'badge-light-warning'
  }

  if (severity === 'danger') {
    return 'badge-light-danger'
  }

  return 'badge-light-primary'
}

const formatInteger = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-'
  }

  return value.toLocaleString('tr-TR')
}

const ProgramOutcomeAchievementPage = () => {
  const [report, setReport] = useState<Report>(emptyReport)
  const [organizationId, setOrganizationId] = useState<number | ''>('')
  const [year, setYear] = useState<number | ''>('')
  const [activeTab, setActiveTab] = useState<'outcomes' | 'students'>('outcomes')
  const [loading, setLoading] = useState(false)
  const [savingCycle, setSavingCycle] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [studentSearch, setStudentSearch] = useState('')
  const [cycleDraft, setCycleDraft] = useState<CycleDraft | null>(null)

  const loadReport = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const params: Record<string, number> = {}
      if (organizationId !== '') {
        params.organizationId = organizationId
      }
      if (year !== '') {
        params.year = year
      }

      const response = await axios.get<Report>(`${API_URL}/Custom/GetProgramOutcomeAchievementReport`, {
        params,
      })
      const nextReport = response.data || emptyReport
      setReport(nextReport)

      if (nextReport.organizationId && nextReport.organizationId !== organizationId) {
        setOrganizationId(nextReport.organizationId)
      }

      if (nextReport.year && nextReport.year !== year) {
        setYear(nextReport.year)
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Program çıktısı raporu yüklenemedi.')
    } finally {
      setLoading(false)
    }
  }, [organizationId, year])

  useEffect(() => {
    loadReport()
  }, [loadReport])

  const selectedOrganizationYears = useMemo(() => {
    const selected = report.organizations.find((organization) => organization.id === organizationId)
    return selected?.years?.length ? selected.years : report.years
  }, [organizationId, report.organizations, report.years])

  const cyclesByOutcome = useMemo(() => {
    return report.cycles.reduce<Record<number, Cycle[]>>((acc, cycle) => {
      if (!acc[cycle.organizationOutcomeId]) {
        acc[cycle.organizationOutcomeId] = []
      }
      acc[cycle.organizationOutcomeId].push(cycle)
      return acc
    }, {})
  }, [report.cycles])

  const filteredStudents = useMemo(() => {
    const search = studentSearch.trim().toLocaleLowerCase('tr-TR')
    if (!search) {
      return report.students
    }

    return report.students.filter((student) => {
      const haystack = `${student.studentNumber || ''} ${student.studentName || ''}`.toLocaleLowerCase(
        'tr-TR'
      )
      return haystack.includes(search)
    })
  }, [report.students, studentSearch])

  const chartData = useMemo(() => {
    const labels = report.outcomes.map((outcome) => `PÇ ${outcome.order}`)
    const values = report.outcomes.map((outcome) => outcome.achievementPercent ?? 0)
    const colors = report.outcomes.map((outcome) => {
      if (outcome.status === 'low') {
        return 'rgba(220, 53, 69, 0.72)'
      }
      if (outcome.status === 'watch') {
        return 'rgba(255, 193, 7, 0.72)'
      }
      if (outcome.status === 'met') {
        return 'rgba(25, 135, 84, 0.72)'
      }
      return 'rgba(108, 117, 125, 0.45)'
    })

    return {
      labels,
      datasets: [
        {
          label: 'Ulaşılma yüzdesi',
          data: values,
          backgroundColor: colors,
          borderColor: colors.map((color) => color.replace('0.72', '1').replace('0.45', '1')),
          borderWidth: 1,
        },
      ],
    }
  }, [report.outcomes])

  const chartOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          min: 0,
          max: 100,
          ticks: {
            callback: (value: number) => `${value}%`,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: (items: any[]) => {
              const index = items?.[0]?.dataIndex ?? 0
              const outcome = report.outcomes[index]
              return outcome ? `PÇ-${outcome.order}: ${outcome.name || ''}` : ''
            },
            label: (item: any) => `Ulaşılma: ${formatPercent(item.parsed.y)}`,
          },
        },
      },
    }),
    [report.outcomes]
  )

  const thresholdTarget = report.summary?.targetPercent ?? DEFAULT_TARGET_PERCENT
  const thresholdWatchLower = report.summary?.watchLowerBoundPercent ?? WATCH_LOWER_PERCENT

  const printReport = useCallback(() => {
    window.print()
  }, [])

  const openCycleModal = (outcome: ProgramOutcome, cycle?: Cycle) => {
    setCycleDraft({
      id: cycle?.id,
      organizationOutcomeId: outcome.id,
      title: cycle?.title || buildCycleTitle(outcome),
      targetPercent: cycle?.targetPercent || outcome.targetPercent || thresholdTarget,
      currentPercent: outcome.achievementPercent ?? cycle?.currentPercent ?? null,
      status: cycle?.status || 'Planlandı',
      plan: cycle?.plan || '',
      do: cycle?.do || '',
      check: cycle?.check || '',
      act: cycle?.act || '',
    })
  }

  const saveCycle = async () => {
    if (!cycleDraft || !report.organizationId || !report.year) {
      return
    }

    setSavingCycle(true)
    setError(null)
    try {
      await axios.post(`${API_URL}/Custom/SaveProgramOutcomeAchievementCycle`, {
        ...cycleDraft,
        organizationId: report.organizationId,
        year: report.year,
      })
      setCycleDraft(null)
      await loadReport()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'PUKÖ kaydı kaydedilemedi.')
    } finally {
      setSavingCycle(false)
    }
  }

  const selectedOutcomeForDraft = cycleDraft
    ? report.outcomes.find((outcome) => outcome.id === cycleDraft.organizationOutcomeId)
    : null

  return (
    <>
      <PageTitle breadcrumbs={[]}>Program Çıktısı Sağlanma Düzeyi</PageTitle>
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }

          .program-outcome-print-area,
          .program-outcome-print-area * {
            visibility: visible !important;
          }

          .program-outcome-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            padding: 0 !important;
          }

          .program-outcome-print-hidden,
          .program-outcome-print-hidden * {
            display: none !important;
            visibility: hidden !important;
          }

          .program-outcome-print-title {
            display: block !important;
          }

          .program-outcome-print-area .card {
            border: 1px solid #e4e6ef !important;
            box-shadow: none !important;
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .program-outcome-print-area .table-responsive {
            overflow: visible !important;
          }

          .program-outcome-print-area table {
            font-size: 10px !important;
          }

          .modal,
          .modal-backdrop {
            display: none !important;
          }

          @page {
            size: A4 landscape;
            margin: 12mm;
          }
        }
      `}</style>

      <div className='d-flex flex-column gap-6 program-outcome-print-area'>
        <div className='d-none program-outcome-print-title'>
          <h2 className='fw-bold mb-1'>Program Çıktısı Sağlanma Düzeyi</h2>
          <div className='text-muted'>
            {report.organizationName || 'Program'} / {report.year || '-'}
          </div>
        </div>

        <div className='card program-outcome-print-hidden'>
          <div className='card-body d-flex flex-column flex-xl-row gap-4 align-items-xl-end'>
            <div className='flex-grow-1'>
              <label className='form-label fw-semibold'>Program</label>
              <select
                className='form-select'
                value={organizationId}
                onChange={(event) => {
                  const value = Number(event.target.value)
                  setOrganizationId(value || '')
                  setYear('')
                }}
                disabled={loading}
              >
                {report.organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {organization.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{width: 180}}>
              <label className='form-label fw-semibold'>Yıl</label>
              <select
                className='form-select'
                value={year}
                onChange={(event) => setYear(Number(event.target.value) || '')}
                disabled={loading}
              >
                {selectedOrganizationYears.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <button
              className='btn btn-light-primary'
              type='button'
              onClick={() => loadReport()}
              disabled={loading}
            >
              <i className='bi bi-arrow-clockwise me-2'></i>
              Yenile
            </button>
            <button
              className='btn btn-light-success'
              type='button'
              onClick={printReport}
              disabled={report.outcomes.length === 0}
            >
              <i className='bi bi-file-earmark-pdf me-2'></i>
              PDF çıktısı
            </button>
          </div>
        </div>

        {error && <div className='alert alert-danger mb-0 program-outcome-print-hidden'>{error}</div>}

        {loading && (
          <div className='alert alert-primary d-flex align-items-center mb-0 program-outcome-print-hidden'>
            <span className='spinner-border spinner-border-sm me-3' role='status' aria-hidden='true'></span>
            <div>
              <div className='fw-semibold'>Rapor hazırlanıyor</div>
              <div>
                Sınav sonuçları ve program çıktısı kırılımları hesaplanıyor.
              </div>
            </div>
          </div>
        )}

        <div className='d-flex flex-wrap gap-3 program-outcome-print-hidden'>
          <button
            className={`btn ${activeTab === 'outcomes' ? 'btn-primary' : 'btn-light'}`}
            type='button'
            onClick={() => setActiveTab('outcomes')}
          >
            Program Çıktıları
          </button>
          <button
            className={`btn ${activeTab === 'students' ? 'btn-primary' : 'btn-light'}`}
            type='button'
            onClick={() => setActiveTab('students')}
          >
            Öğrenci Bazlı Rapor
          </button>
        </div>

        {activeTab === 'outcomes' && (
          <>
            {report.summary && (
              <div className='card'>
                <div className='card-header border-0 pt-6'>
                  <div>
                    <h3 className='card-title fw-bold mb-1'>Özet Veri</h3>
                    <div className='text-muted'>
                      Başarı eşiği {formatPercent(thresholdTarget, 0)}, izleme bandı{' '}
                      {formatPercent(thresholdWatchLower, 0)}-{formatPercent(thresholdTarget, 0)}
                    </div>
                  </div>
                </div>
                <div className='card-body'>
                  <div className='row g-4 mb-6'>
                    {report.summary.insights.map((insight) => (
                      <div className='col-md-6 col-xl-3' key={insight.title}>
                        <div className='border rounded p-4 h-100'>
                          <div className='d-flex justify-content-between align-items-start gap-3 mb-3'>
                            <div className='fw-semibold'>{insight.title}</div>
                            <span className={`badge ${insightBadgeClass(insight.severity)}`}>{insight.value}</span>
                          </div>
                          <div className='text-muted'>{insight.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className='d-flex flex-wrap gap-3 mb-6'>
                    <span className='badge badge-light-success'>Başarılı: {report.summary.metOutcomeCount}</span>
                    <span className='badge badge-light-warning'>İzlemede: {report.summary.watchOutcomeCount}</span>
                    <span className='badge badge-light-danger'>Eşik altı: {report.summary.lowOutcomeCount}</span>
                    <span className='badge badge-light-secondary'>Veri yok: {report.summary.noDataOutcomeCount}</span>
                    <span className='badge badge-light-primary'>
                      Sonuç satırı: {formatInteger(report.summary.resultRowCount)}
                    </span>
                  </div>

                  <div className='table-responsive'>
                    <table className='table table-row-dashed align-middle mb-0'>
                      <thead>
                        <tr className='fw-bold text-muted'>
                          <th>PÇ</th>
                          <th>Ders</th>
                          <th>Tür</th>
                          <th>Ortalama</th>
                          <th>Öğrenci</th>
                          <th>Sınav</th>
                          <th>Soru</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.summary.lowCourseFactors.map((factor) => (
                          <tr
                            key={`${factor.organizationOutcomeId}-${factor.courseCode}-${factor.activityType}-${factor.rawPercent}`}
                          >
                            <td className='fw-semibold'>PÇ-{factor.outcomeOrder}</td>
                            <td style={{minWidth: 260}}>
                              <div className='fw-semibold'>{factor.courseCode || '-'}</div>
                              <div className='text-muted'>{factor.courseName || '-'}</div>
                            </td>
                            <td>{factor.activityTypeLabel}</td>
                            <td>
                              <span className={`fw-bold ${percentTextClass(factor.rawPercent, thresholdTarget, thresholdWatchLower)}`}>
                                {formatPercent(factor.rawPercent)}
                              </span>
                            </td>
                            <td>{factor.studentCount}</td>
                            <td>{factor.examCount}</td>
                            <td>{factor.questionCount}</td>
                          </tr>
                        ))}
                        {report.summary.lowCourseFactors.length === 0 && (
                          <tr>
                            <td className='text-center text-muted py-8' colSpan={7}>
                              Eşik altı ders kırılımı bulunamadı.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <div className='card'>
              <div className='card-header border-0 pt-6'>
                <div>
                  <h3 className='card-title fw-bold mb-1'>{report.organizationName || 'Program'}</h3>
                  <div className='text-muted'>{report.year || '-'} yılı program çıktısı ulaşılma yüzdeleri</div>
                </div>
              </div>
              <div className='card-body'>
                <div style={{height: 360}}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            </div>

            <div className='card'>
              <div className='card-body p-0'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed align-middle mb-0'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='ps-6'>Çıktı</th>
                        <th>Hedef</th>
                        <th>Ara sınav</th>
                        <th>Final</th>
                        <th>Ulaşılma</th>
                        <th>Kanıt</th>
                        <th>Durum</th>
                        <th className='text-end pe-6 program-outcome-print-hidden'>PUKÖ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.outcomes.map((outcome) => {
                        const outcomeCycles = cyclesByOutcome[outcome.id] || []
                        return (
                          <tr key={outcome.id}>
                            <td className='ps-6' style={{minWidth: 320}}>
                              <div className='fw-semibold'>PÇ-{outcome.order}</div>
                              <div className='text-muted'>{outcome.name}</div>
                            </td>
                            <td>{formatPercent(outcome.targetPercent)}</td>
                            <td>
                              <span className={percentTextClass(outcome.midtermPercent, thresholdTarget, thresholdWatchLower)}>
                                {formatPercent(outcome.midtermPercent)}
                              </span>
                            </td>
                            <td>
                              <span className={percentTextClass(outcome.finalPercent, thresholdTarget, thresholdWatchLower)}>
                                {formatPercent(outcome.finalPercent)}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`fw-bold ${percentTextClass(
                                  outcome.achievementPercent,
                                  thresholdTarget,
                                  thresholdWatchLower
                                )}`}
                              >
                                {formatPercent(outcome.achievementPercent)}
                              </span>
                            </td>
                            <td>
                              <div>{outcome.examCount} sınav</div>
                              <div className='text-muted'>{outcome.questionBankCount} soru</div>
                            </td>
                            <td>
                              <span className={`badge ${statusBadgeClass(outcome.status)}`}>
                                {outcome.statusLabel}
                              </span>
                            </td>
                            <td className='text-end pe-6 program-outcome-print-hidden'>
                              <button
                                className='btn btn-sm btn-light-primary'
                                type='button'
                                onClick={() => openCycleModal(outcome, outcomeCycles[0])}
                              >
                                <i className='bi bi-plus-circle me-2'></i>
                                {outcomeCycles.length > 0 ? 'Güncelle' : 'Başlat'}
                              </button>
                              {outcome.openCycleCount > 0 && (
                                <div className='text-muted mt-2'>{outcome.openCycleCount} açık döngü</div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                      {report.outcomes.length === 0 && (
                        <tr>
                          <td className='text-center text-muted py-10' colSpan={8}>
                            Kayıt bulunamadı.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className='card'>
              <div className='card-header border-0 pt-6'>
                <h3 className='card-title fw-bold'>Sonuç Girilmiş Sınavlar</h3>
              </div>
              <div className='card-body p-0'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed align-middle mb-0'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='ps-6'>Ders</th>
                        <th>Sınav</th>
                        <th>Tür</th>
                        <th>Ağırlık</th>
                        <th>Öğrenci</th>
                        <th className='pe-6'>Soru</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.exams.map((exam) => (
                        <tr key={exam.examId}>
                          <td className='ps-6'>
                            <div className='fw-semibold'>{exam.courseCode || '-'}</div>
                            <div className='text-muted'>{exam.courseName || '-'}</div>
                          </td>
                          <td>{exam.examName || '-'}</td>
                          <td>{exam.activityTypeLabel || exam.examTypeName || '-'}</td>
                          <td>{exam.weight}</td>
                          <td>{exam.studentCount}</td>
                          <td className='pe-6'>{exam.questionCount}</td>
                        </tr>
                      ))}
                      {report.exams.length === 0 && (
                        <tr>
                          <td className='text-center text-muted py-10' colSpan={6}>
                            Sonuç girilmiş sınav bulunamadı.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'students' && (
          <div className='card'>
            <div className='card-header border-0 pt-6'>
              <div>
                <h3 className='card-title fw-bold mb-1'>Öğrenci Bazlı Ulaşılma</h3>
                <div className='text-muted'>{filteredStudents.length} öğrenci</div>
              </div>
              <div className='card-toolbar program-outcome-print-hidden'>
                <input
                  className='form-control'
                  style={{width: 260}}
                  value={studentSearch}
                  onChange={(event) => setStudentSearch(event.target.value)}
                  placeholder='Öğrenci ara'
                />
              </div>
            </div>
            <div className='card-body p-0'>
              <div className='table-responsive'>
                <table className='table table-row-dashed align-middle mb-0'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='ps-6' style={{minWidth: 220}}>
                        Öğrenci
                      </th>
                      <th>Ortalama</th>
                      {report.outcomes.map((outcome) => (
                        <th key={outcome.id} title={outcome.name || ''}>
                          PÇ-{outcome.order}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => (
                      <tr key={student.studentId}>
                        <td className='ps-6'>
                          <div className='fw-semibold'>{student.studentName || '-'}</div>
                          <div className='text-muted'>{student.studentNumber || '-'}</div>
                        </td>
                        <td>
                          <span
                            className={`fw-bold ${percentTextClass(
                              student.averageAchievementPercent,
                              thresholdTarget,
                              thresholdWatchLower
                            )}`}
                          >
                            {formatPercent(student.averageAchievementPercent)}
                          </span>
                        </td>
                        {report.outcomes.map((outcome) => {
                          const studentOutcome = student.outcomes.find(
                            (item) => item.organizationOutcomeId === outcome.id
                          )
                          return (
                            <td key={outcome.id}>
                              <span
                                className={percentTextClass(
                                  studentOutcome?.achievementPercent,
                                  thresholdTarget,
                                  thresholdWatchLower
                                )}
                                title={`Ara: ${formatPercent(studentOutcome?.midtermPercent)} | Final: ${formatPercent(
                                  studentOutcome?.finalPercent
                                )}`}
                              >
                                {formatPercent(studentOutcome?.achievementPercent, 0)}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                    {filteredStudents.length === 0 && (
                      <tr>
                        <td className='text-center text-muted py-10' colSpan={2 + report.outcomes.length}>
                          Öğrenci sonucu bulunamadı.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {cycleDraft && selectedOutcomeForDraft && (
        <>
          <div className='modal fade show d-block' tabIndex={-1} role='dialog'>
            <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <div>
                    <h5 className='modal-title mb-1'>PUKÖ Döngüsü</h5>
                    <div className='text-muted'>
                      PÇ-{selectedOutcomeForDraft.order} · {formatPercent(selectedOutcomeForDraft.achievementPercent)}
                    </div>
                  </div>
                  <button className='btn btn-icon btn-sm btn-light' type='button' onClick={() => setCycleDraft(null)}>
                    <i className='bi bi-x-lg'></i>
                  </button>
                </div>
                <div className='modal-body'>
                  <div className='row g-4'>
                    <div className='col-md-8'>
                      <label className='form-label fw-semibold'>Başlık</label>
                      <input
                        className='form-control'
                        value={cycleDraft.title}
                        onChange={(event) => setCycleDraft({...cycleDraft, title: event.target.value})}
                      />
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label fw-semibold'>Hedef</label>
                      <input
                        className='form-control'
                        type='number'
                        min={0}
                        max={100}
                        value={cycleDraft.targetPercent}
                        onChange={(event) =>
                          setCycleDraft({...cycleDraft, targetPercent: Number(event.target.value)})
                        }
                      />
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label fw-semibold'>Durum</label>
                      <select
                        className='form-select'
                        value={cycleDraft.status}
                        onChange={(event) => setCycleDraft({...cycleDraft, status: event.target.value})}
                      >
                        <option value='Planlandı'>Planlandı</option>
                        <option value='Uygulanıyor'>Uygulanıyor</option>
                        <option value='Kontrol Ediliyor'>Kontrol Ediliyor</option>
                        <option value='Önlem Alındı'>Önlem Alındı</option>
                        <option value='Tamamlandı'>Tamamlandı</option>
                      </select>
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label fw-semibold'>Mevcut</label>
                      <input className='form-control' value={formatPercent(cycleDraft.currentPercent)} disabled />
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label fw-semibold'>Program</label>
                      <input className='form-control' value={`${report.organizationName || '-'} / ${report.year || '-'}`} disabled />
                    </div>
                    <div className='col-12'>
                      <label className='form-label fw-semibold'>Planla</label>
                      <textarea
                        className='form-control'
                        rows={3}
                        value={cycleDraft.plan}
                        onChange={(event) => setCycleDraft({...cycleDraft, plan: event.target.value})}
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fw-semibold'>Uygula</label>
                      <textarea
                        className='form-control'
                        rows={3}
                        value={cycleDraft.do}
                        onChange={(event) => setCycleDraft({...cycleDraft, do: event.target.value})}
                      />
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fw-semibold'>Kontrol Et</label>
                      <textarea
                        className='form-control'
                        rows={3}
                        value={cycleDraft.check}
                        onChange={(event) => setCycleDraft({...cycleDraft, check: event.target.value})}
                      />
                    </div>
                    <div className='col-12'>
                      <label className='form-label fw-semibold'>Önlem Al</label>
                      <textarea
                        className='form-control'
                        rows={3}
                        value={cycleDraft.act}
                        onChange={(event) => setCycleDraft({...cycleDraft, act: event.target.value})}
                      />
                    </div>
                  </div>
                </div>
                <div className='modal-footer'>
                  <button className='btn btn-light' type='button' onClick={() => setCycleDraft(null)}>
                    Vazgeç
                  </button>
                  <button className='btn btn-primary' type='button' onClick={saveCycle} disabled={savingCycle}>
                    {savingCycle ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show'></div>
        </>
      )}
    </>
  )
}

export default ProgramOutcomeAchievementPage
